import axios from 'axios'

// The conversation lives in the browser, not the Rails session: the session
// cookie is 4KB and a transcript would not fit. sessionStorage survives the
// navigation from the homepage to a results page, which is what makes the
// conversation continue across the hand-off.
//
// Used by the concierge mode of the search band
// (components/product/grid2/enquiry-form), which renders on the homepage card,
// the occasion landing pages, the category modal and the results grid.
const TRANSCRIPT_KEY = 'bookaparty:concierge'
const ENDPOINT       = '/api/v1/public/concierge'

export const readTranscript = () => {
	try {
		const stored = JSON.parse( sessionStorage.getItem( TRANSCRIPT_KEY ) )
		return Array.isArray( stored ) ? stored : []
	} catch ( e ) {
		// Private browsing, or somebody hand-edited the key.
		return []
	}
}

const writeTranscript = ( turns ) => {
	try {
		sessionStorage.setItem( TRANSCRIPT_KEY, JSON.stringify( turns ) )
	} catch ( e ) {
		// Quota or private browsing — this turn still works, it just won't carry over.
	}
}

export const clearTranscript = () => {
	try {
		sessionStorage.removeItem( TRANSCRIPT_KEY )
	} catch ( e ) {}
}

// Feed the model back its own structured output so the next turn refines the
// search rather than starting from scratch.
const assistantTurn = ( data ) => ({
	role: 'assistant',
	content: JSON.stringify( Object.assign( { reply: data.reply }, data.filters ) )
})

// GTM already carries the ecommerce funnel (view_item, begin_checkout, purchase),
// so pushing the concierge turn onto the same dataLayer is all GA4 needs to
// compare conversion for sessions that used the concierge against those that
// didn't.
//
// ponytail: search only, not the tab click — a tab opened and abandoned is noise
// in a conversion comparison. Add a concierge_open push in enquiry-form if the
// drop-off between opening and asking turns out to matter.
const track = ( text, turns, data ) => {
	window.dataLayer = window.dataLayer || []
	window.dataLayer.push({
		event: 'concierge_search',
		concierge_query: text,
		concierge_turn: turns.filter( t => t.role === 'user' ).length,
		// Empty when the concierge asked a follow-up instead of searching.
		concierge_destination: data.path || ''
	})
}

const errorFrom = ( err ) => {
	const errors = err.response && err.response.data && err.response.data.errors
	return new Error( errors && errors[0] ? errors[0] : "Something went wrong. Please try again." )
}

// Sends one turn. Resolves with the response body and the turns it was built
// from; the transcript is only persisted once the call succeeds, so a failed
// turn does not poison the conversation.
//
// `path` is the page being viewed. Without it a refinement that names no city
// ("best for summer bbq") reads as "no location", and the guest gets thrown off
// /stag-do-ideas/prague onto /stag-do-ideas. The server resolves it against the
// database, so it is never trusted as free text.
//
// `productIds` are the results on screen, so the guest can ask about them by name
// ("how far apart are the cocktail making and the afternoon tea"). Only ids travel
// — the server reads the names and coordinates back out of the database. Omitted
// on the homepage and the landing pages, which have no results.
export const ask = ( token, text, path, productIds ) => {

	const turns = readTranscript().concat([{ role: 'user', content: text }])

	return axios.post( ENDPOINT, {
		authenticity_token: token,
		messages: turns,
		path: path,
		product_ids: productIds || []
	}).then( response => {
		const data = response.data
		writeTranscript( turns.concat([ assistantTurn( data ) ]) )
		track( text, turns, data )
		return data
	}).catch( err => {
		throw errorFrom( err )
	})
}
